import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover, { PopoverProps } from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';
import moment from 'moment';

import { IJobDTO, IJobValueDTO } from 'src/api/dtos';
import CommonJobRepository from 'src/api/repositories/common/v1/job.repository';
import { ChevronDownIcon, SearchIcon, Tooltip } from 'src/shared/components';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { LocalStorageEnums } from 'src/shared/enums';
import { useInfiniteScroll, useLocalStorage } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { handleScrollEnd } from 'src/shared/utils';

const RECENT_JOBS_COUNT = 5;
const SCROLL_OFFSET = 100;

interface Props extends Omit<PopoverProps, 'onSelect'> {
  level: number;
  startDate?: string;
  endDate?: string;
  selected?: IJobDTO | null;
  onSelect?: (job: IJobDTO) => void;
}

export const JobPopover: FC<Props> = ({ level, startDate, endDate, selected, onSelect, open, ...props }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const { value: recents, setValue: setRecents } = useLocalStorage<IJobDTO[]>(
    LocalStorageEnums.TIMESHEET_RECENT_JOBS,
    [] as IJobDTO[],
  );

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  const levelRecents = useMemo(() => recents?.filter((recent) => recent.level === level), [recents]);
  const isCollapseDisabled = !!searchTerm || !levelRecents?.length;
  const isFullListOpen = !collapsed || isCollapseDisabled;

  const {
    collection: jobValues,
    paginatedData: paginatedJobs,
    handleLoadMore,
    isLoadingMore,
    isRefreshing,
  } = useInfiniteScroll<IJobValueDTO>({
    useQuery: CommonJobRepository.endpoints.useGetJobsQuery,
    apiParams: {
      jobLevel: level,
      startDate: startDate || moment().format(API_DATE_FORMAT),
      endDate,
      searchTerm,
      perPage: 10,
    },
    skip: !open,
  });

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    debounceSearchTermChange(event.target.value);
    setInputValue(event.target.value);
  };

  const handleSelect = (job: IJobDTO) => {
    onSelect?.(job);
    setInputValue('');
    setSearchTerm('');

    if (recents?.some((recent) => recent.values[0]?.value === job.values[0]?.value)) {
      return;
    }

    const levelRecents = recents?.filter((recent) => recent.level === level) ?? [];
    const otherRecents = recents?.filter((recent) => recent.level !== level) ?? [];
    const updatedLevelRecents = [job, ...levelRecents].slice(0, RECENT_JOBS_COUNT);

    setRecents([...updatedLevelRecents, ...otherRecents]);
  };

  return (
    <Popover open={open} {...props}>
      <Stack borderRadius={1} overflow="hidden">
        <TextField
          value={inputValue}
          onChange={handleInputValueChange}
          placeholder={t('global.selectOrSearchValue')}
          InputProps={{ endAdornment: <SearchIcon color={theme.palette.action.active} /> }}
          sx={{ padding: 1.5 }}
        />
      </Stack>
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" padding="0 12px">
        <Typography fontSize="0.75rem" fontWeight={600}>
          {t('global.recents')}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCollapsed(!collapsed)}
          className={`collapsible-label ${!isFullListOpen ? 'open' : ''}`}
          disabled={isCollapseDisabled}
        >
          <ChevronDownIcon className="collapsible-chevron-icon" />
        </IconButton>
      </Stack>
      <Collapse in={collapsed && !searchTerm}>
        {!levelRecents?.length && (
          <MenuItem disabled>
            <Typography fontSize="0.75rem">{t('global.none')}</Typography>
          </MenuItem>
        )}
        {levelRecents?.map((recent) => {
          const jobValue = recent.values[0] as IJobValueDTO | undefined;
          const label = jobValue ? `${jobValue.value} - ${jobValue.description}` : '';

          if (!jobValue) {
            return null;
          }

          return (
            <MenuItem
              key={jobValue.value}
              selected={jobValue.value === selected?.values[0]?.value}
              onClick={() => handleSelect(recent)}
            >
              <Tooltip title={<Typography fontSize="0.75rem">{label}</Typography>} showIcon={false}>
                <Typography fontSize="0.75rem" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                  {label}
                </Typography>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Collapse>
      <Divider />
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" padding="0 12px">
        <Typography fontSize="0.75rem" fontWeight={600}>
          {t('global.fullList')}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCollapsed(!collapsed)}
          className={`collapsible-label ${isFullListOpen ? 'open' : ''}`}
          disabled={isCollapseDisabled}
        >
          <ChevronDownIcon className="collapsible-chevron-icon" />
        </IconButton>
      </Stack>
      <Collapse in={isFullListOpen}>
        <Stack
          maxHeight={300}
          overflow="auto"
          paddingBottom={1.5}
          onScroll={(event) =>
            handleScrollEnd(event, handleLoadMore, {
              loading: isRefreshing || isLoadingMore,
              scrollOffset: SCROLL_OFFSET,
            })
          }
        >
          {isRefreshing || !paginatedJobs ? (
            <Skeleton
              className="rounded"
              variant="rectangular"
              animation="wave"
              height={144}
              sx={{ margin: '0 8px' }}
            />
          ) : (
            <>
              {jobValues.map((jobValue) => {
                const label = `${jobValue.value} - ${jobValue.description}`;

                return (
                  <Tooltip
                    key={jobValue.value}
                    title={<Typography fontSize="0.75rem">{label}</Typography>}
                    showIcon={false}
                  >
                    <MenuItem
                      sx={{ whiteSpace: 'normal' }}
                      selected={jobValue.value === selected?.values[0]?.value}
                      onClick={() => handleSelect({ level, name: paginatedJobs?.name ?? '', values: [jobValue] })}
                    >
                      <Typography fontSize="0.75rem">{label}</Typography>
                    </MenuItem>
                  </Tooltip>
                );
              })}
              {!jobValues.length && (
                <MenuItem disabled>
                  <Typography fontSize="0.75rem">{t('global.noOptions')}</Typography>
                </MenuItem>
              )}
            </>
          )}
        </Stack>
      </Collapse>
    </Popover>
  );
};
